import React, { useEffect, useState } from "react";
import { api, formatBRL } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, TrendingUp, ShoppingBag, Clock, Trophy, CheckCircle2, Package } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const StatCard = ({ icon: Icon, label, value, hint, tone, testid }) => (
  <Card className="border-stone-200 rounded-2xl card-warm-shadow" data-testid={testid}>
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-stone-500">{label}</div>
          <div className="font-display text-3xl font-bold mt-2 text-stone-900">{value}</div>
          {hint && <div className="text-xs text-stone-500 mt-1">{hint}</div>}
        </div>
        <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${tone}`}>
          <Icon className="h-5 w-5 text-white" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const statusBadge = {
  paid: <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-100">Pago</Badge>,
  pending: <Badge className="bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-100">Pendente</Badge>,
  cancelled: <Badge className="bg-stone-200 text-stone-700 border-stone-300 hover:bg-stone-200">Cancelado</Badge>,
};

export default function DashboardOverview() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dashboard/stats");
        setStats(data);
      } catch {
        setStats({ total_orders: 0, paid_orders: 0, pending_orders: 0, total_revenue: 0, pending_revenue: 0, top_items: [], recent_orders: [] });
      }
    })();
  }, []);

  if (!stats) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-overview">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-orange-700 font-semibold">Painel</div>
        <h1 className="font-display text-4xl font-bold mt-2 text-stone-900">Visão geral</h1>
        <p className="text-stone-600 mt-2">Acompanhe pedidos e vendas do 3º Encontrão em tempo real.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard
          icon={TrendingUp}
          label="Receita paga"
          value={formatBRL(stats.total_revenue)}
          hint={`+ ${formatBRL(stats.pending_revenue)} pendente`}
          tone="bg-gradient-to-br from-emerald-500 to-emerald-700"
          testid="stat-revenue"
        />
        <StatCard
          icon={ShoppingBag}
          label="Pedidos totais"
          value={stats.total_orders}
          hint={`${stats.paid_orders} pagos`}
          tone="bg-gradient-to-br from-red-700 to-orange-600"
          testid="stat-orders"
        />
        <StatCard
          icon={Clock}
          label="Pendentes"
          value={stats.pending_orders}
          hint="Aguardando confirmação"
          tone="bg-gradient-to-br from-amber-500 to-yellow-600"
          testid="stat-pending"
        />
        <StatCard
          icon={CheckCircle2}
          label="Confirmados"
          value={stats.paid_orders}
          hint="Pagamentos confirmados"
          tone="bg-gradient-to-br from-stone-700 to-stone-900"
          testid="stat-paid"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-stone-200 rounded-2xl">
          <CardHeader>
            <CardTitle className="font-display text-2xl flex items-center gap-2">
              <ShoppingBag className="h-5 w-5 text-orange-600" /> Últimos pedidos
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats.recent_orders.length === 0 ? (
              <div className="py-10 text-center text-stone-500 text-sm">Nenhum pedido ainda.</div>
            ) : (
              <div className="divide-y divide-stone-200">
                {stats.recent_orders.map((o) => (
                  <div key={o.id} className="py-4 flex items-center justify-between" data-testid={`recent-order-${o.id}`}>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-semibold text-stone-900">{o.customer_name}</div>
                        {o.source === "subscription" && (
                          <Badge className="bg-purple-100 text-purple-800 border-purple-200 hover:bg-purple-100 text-[10px] uppercase tracking-widest" data-testid={`recent-source-${o.id}`}>Inscrição</Badge>
                        )}
                      </div>
                      <div className="text-xs text-stone-500 mt-0.5">
                        {o.items.reduce((s, i) => s + i.quantity, 0)} item(ns) · {new Date(o.created_at).toLocaleString("pt-BR")}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold">{formatBRL(o.total)}</div>
                      <div className="mt-1">{statusBadge[o.status]}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-stone-200 rounded-2xl">
          <CardHeader>
            <CardTitle className="font-display text-2xl flex items-center gap-2">
              <Trophy className="h-5 w-5 text-amber-500" /> Mais vendidos
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats.top_items.length === 0 ? (
              <div className="py-10 text-center text-stone-500 text-sm">Sem dados ainda.</div>
            ) : (
              <ol className="space-y-3">
                {stats.top_items.map((it, idx) => (
                  <li key={it.name} className="flex items-center gap-3" data-testid={`top-item-${idx}`}>
                    <div className="h-8 w-8 rounded-lg flame-gradient text-white flex items-center justify-center text-sm font-bold">
                      {idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{it.name}</div>
                    </div>
                    <div className="text-sm font-semibold text-stone-700">{it.quantity}</div>
                  </li>
                ))}
              </ol>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="border-stone-200 rounded-2xl">
        <CardHeader>
          <CardTitle className="font-display text-2xl flex items-center gap-2">
            <Package className="h-5 w-5 text-orange-600" /> Itens vendidos
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(!stats.items_breakdown || stats.items_breakdown.every((c) => c.total === 0)) ? (
            <div className="py-10 text-center text-stone-500 text-sm">Sem vendas registradas ainda.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="items-breakdown-grid">
              {(stats.items_breakdown || []).map((cat) => (
                <div
                  key={cat.key}
                  className="rounded-2xl border border-stone-200 bg-white p-5 hover:border-orange-300 transition-colors"
                  data-testid={`item-cat-${cat.key}`}
                >
                  <div className="flex items-start justify-between gap-3 pb-3 border-b border-stone-100">
                    <div className="min-w-0">
                      <div className="text-xs uppercase tracking-widest text-orange-700 font-semibold">Categoria</div>
                      <div className="font-display text-xl font-bold text-stone-900 mt-1">{cat.name}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Total geral</div>
                      <div
                        className="font-display text-4xl font-bold brand-text leading-none mt-1"
                        data-testid={`item-cat-${cat.key}-total`}
                      >
                        {cat.total}
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-col gap-1.5">
                    {cat.breakdown.map((b) => {
                      const goal = ((stats.sales_goals || {})[cat.key] || {})[b.label];
                      const goalNum = typeof goal === "number" ? goal : (parseInt(goal, 10) || 0);
                      const pct = goalNum > 0 ? Math.min(100, Math.round((b.quantity / goalNum) * 100)) : 0;
                      const barColor = pct >= 100
                        ? "bg-emerald-500"
                        : pct >= 70
                        ? "bg-amber-500"
                        : "bg-orange-500";
                      return (
                        <div
                          key={b.label}
                          className="py-1.5 px-2.5 rounded-md hover:bg-orange-50/60 transition-colors"
                          data-testid={`item-cat-${cat.key}-${b.label}`}
                        >
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-stone-700">{b.label}</span>
                            <span className={`font-mono font-bold tabular-nums ${b.quantity > 0 ? "text-stone-900" : "text-stone-300"}`}>
                              {goalNum > 0 ? (
                                <>
                                  {b.quantity}
                                  <span className="text-stone-400 font-normal"> / {goalNum}</span>
                                </>
                              ) : (
                                b.quantity
                              )}
                            </span>
                          </div>
                          {goalNum > 0 && (
                            <div className="mt-1.5 h-1.5 bg-stone-100 rounded-full overflow-hidden">
                              <div
                                className={`h-full ${barColor} transition-all`}
                                style={{ width: `${pct}%` }}
                                data-testid={`item-cat-${cat.key}-${b.label}-bar`}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
